import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'

import theme from 'theme'
import { cn, formatPrice } from 'utils'
import { CART_STATE } from 'views/CartV2/consts'
import {
  isAnyFloatingPriceInCart,
  useCartDeliveryAddress,
} from 'views/CartV2/utils'
import { FloatingPriceSmall as FloatingPriceIcon } from 'components/Icons/FloatingPrice'

import SummaryInfoBox from '../SummaryInfoBox'
import messages from '../../messages'

const Content = ({ userData, cartData, isDesktop }) => {
  const { formatMessage } = useIntl()

  const { customerName, paymentMethodCode, paymentTermCode } = userData

  const { totalPrice, tradeLimit, state } = cartData

  const addressFormatted = useCartDeliveryAddress(userData)
  const hasFloatingPrice =
    useMemo(() => isAnyFloatingPriceInCart(cartData), [cartData]) && !isDesktop

  return (
    <div
      data-test-id="cart-summary_content"
      className="flex flex-col gap-4 pt-2 pb-6 lg:p-0"
    >
      <div
        data-test-id="cart-summary_customer-details"
        className="px-2 pb-4 border-b border-grey-300 lg:hidden"
      >
        <p className="text-13">{customerName}</p>
        <p className="text-13 text-grey-800">{addressFormatted}</p>
      </div>
      <div className="flex flex-col gap-1 px-2 pb-4 border-b border-grey-300 lg:px-0">
        <div
          data-test-id="cart-summary_payment-method"
          className="flex justify-between lg:hidden"
        >
          <span className="text-label-13">
            {formatMessage(messages.payment)}
          </span>
          <span className="text-label-13sb capitalize">{`${paymentMethodCode}, ${paymentTermCode}`}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-label-13 lg:text-12">
            {formatMessage(messages.totalNet)}
          </span>
          <span className="text-label-13sb lg:text-12 lg:font-semibold">
            {formatPrice(totalPrice.net)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-label-13 lg:text-12">
            {formatMessage(messages.totalVat)}
          </span>
          <span className="text-label-13sb lg:text-12 lg:font-semibold">
            {formatPrice(totalPrice.gross - totalPrice.net)}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-4 px-2 pb-4 border-b border-grey-300 lg:px-0">
        <div className="flex justify-between">
          <span className="text-label-13 lg:text-12">
            {formatMessage(messages.creditLimitLeft)}
          </span>
          <div className="flex gap-2 items-center">
            <div className="w-4 h-4">
              <CircularProgressbar
                value={
                  (tradeLimit.availableGross /
                    (tradeLimit.availableGross + tradeLimit.consumedGross)) *
                  100
                }
                strokeWidth={14}
                styles={buildStyles({
                  pathColor: theme.colors.primary,
                  trailColor: !tradeLimit.availableGross
                    ? theme.colors.orange3
                    : theme.colors.lightGreyHover,
                  strokeLinecap: 'butt',
                })}
              />
            </div>
            <span
              className={cn(
                'text-label-13sb lg:text-12 lg:font-semibold',
                !tradeLimit.availableGross && 'text-orange-900',
              )}
            >
              {formatPrice(tradeLimit.availableGross)}
            </span>
          </div>
        </div>
        {isDesktop && (
          <SummaryInfoBox userData={userData} cartData={cartData} />
        )}
      </div>
      {(state.value !== CART_STATE.BLOCKED || hasFloatingPrice) && (
        <div className="flex flex-col gap-2 px-2 pb-4 border-b border-grey-300 lg:px-0">
          {state.value !== CART_STATE.BLOCKED && (
            <span className="text-13 text-grey-800 lg:text-label-11 lg:text-grey-700">
              {formatMessage(messages.weightedProductInfo)}
            </span>
          )}
          {hasFloatingPrice && (
            <div
              data-test-id="cart-summary_floating-price-info"
              className="flex justify-between items-center"
            >
              <span className="text-13 text-grey-800 lg:text-label-11 lg:text-grey-700">
                {formatMessage(messages.floatingPriceText)}
              </span>
              <FloatingPriceIcon />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Content
